import React, { memo, useEffect, useMemo } from 'react';
import { Grid } from '@material-ui/core';
import { Form, Formik } from 'formik';
import { cloneDeep, set } from 'lodash';
import { useHistory } from 'react-router';
import { StringParam, useQueryParam } from 'use-query-params';
import { object as YupObject, string as YupString } from 'yup';
import { unpackToDate } from '@campfire/hot-date';
import { useCampfireLazyQuery } from '../../../../global/network/useCampfireLazyQuery';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../global/config/useSnackbar';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { useUser } from '../../../../global/auth/useUser';
import { GET_FORM } from '../forms.gql';
import { GetForm, GetFormVariables } from '../__generated__/GetForm';
import { FormBuilderDialogProvider } from './FormBuilderDialogContext';
import { FormBuilderHeader } from './FormBuilderHeader';
import { FormControlsCard, FormControlsContent } from './FormControlsCard';
import { FormDetailsCard } from './FormDetailsCard';
import { deserializeFormItems, FormItemsDeserialize } from './utils';
import { FormListItemsForm } from './FormListItemsForm';
import { AdminConsoleVolunteerProfileHeader } from '../../admin-console/admin-console-content-pages/admin-console-volunteer-profile/AdminConsoleVolunteerProfileHeader';

const FORM_TO_TASK_FIELD_TYPENAME: Record<string, string> = {
  VOLUNTEER_FormMultipleChoiceTaskFieldType: 'VOLUNTEER_MultipleChoiceTaskFieldType',
  VOLUNTEER_FormCheckboxTaskFieldType: 'VOLUNTEER_CheckboxTaskFieldType',
  VOLUNTEER_FormDropdownTaskFieldType: 'VOLUNTEER_DropdownTaskFieldType',
  VOLUNTEER_FormQuizTaskFieldType: 'VOLUNTEER_QuizTaskFieldType',
  VOLUNTEER_FormMatrixTaskFieldType: 'VOLUNTEER_MatrixTaskFieldType',
};

const createFormItem = (formItemId: string, values: FormItemsDeserialize) => {
  const formItem = cloneDeep(values.byId[formItemId]);

  if (formItem.formItemId.startsWith('new')) {
    set(formItem, 'formItemId', undefined);
  }

  if (formItem.__typename === 'VOLUNTEER_FormItemContentType') {
    set(
      formItem,
      'attachments',
      (formItem as any).attachments?.map((attachment: any) => ({
        ...attachment,
        attachmentId: attachment.attachmentId.includes('new') ? undefined : attachment.attachmentId,
      })) || []
    );
  }

  if (formItem.__typename !== 'VOLUNTEER_FormItemFieldType') {
    return formItem;
  }

  const fieldTypename = formItem.field.__typename;

  if (fieldTypename === 'VOLUNTEER_FormMultipleChoiceTaskFieldType') {
    set(
      formItem,
      'field.multipleChoiceOptions',
      formItem.field.multipleChoiceOptions.map((option) => ({
        ...option,
        multipleChoiceFieldOptionId: option.multipleChoiceFieldOptionId.includes('new')
          ? undefined
          : option.multipleChoiceFieldOptionId,
        trigger: {
          ...option.trigger,
          items: (values.byTrigger[option.multipleChoiceFieldOptionId]?.triggerItemIds || []).map(
            (itemId, itemIndex) => ({
              item: createFormItem(itemId, values),
              order: itemIndex,
            })
          ),
        },
      }))
    );
  }

  if (fieldTypename === 'VOLUNTEER_FormCheckboxTaskFieldType') {
    set(
      formItem,
      'field.checkboxOptions',
      formItem.field.checkboxOptions.map((option) => ({
        ...option,
        checkboxFieldOptionId: option.checkboxFieldOptionId.includes('new') ? undefined : option.checkboxFieldOptionId,
        trigger: {
          ...option.trigger,
          items: (values.byTrigger[option.checkboxFieldOptionId]?.triggerItemIds || []).map((itemId, itemIndex) => ({
            item: createFormItem(itemId, values),
            order: itemIndex,
          })),
        },
      }))
    );
  }

  if (fieldTypename === 'VOLUNTEER_FormDropdownTaskFieldType') {
    set(
      formItem,
      'field.dropdownOptions',
      formItem.field.dropdownOptions.map((option) => ({
        ...option,
        dropdownFieldOptionId: option.dropdownFieldOptionId.includes('new') ? undefined : option.dropdownFieldOptionId,
        trigger: {
          ...option.trigger,
          items: (values.byTrigger[option.dropdownFieldOptionId]?.triggerItemIds || []).map((itemId, itemIndex) => ({
            item: createFormItem(itemId, values),
            order: itemIndex,
          })),
        },
      }))
    );
  }

  if (fieldTypename === 'VOLUNTEER_FormQuizTaskFieldType') {
    const correctId = (formItem.field as any).quizTaskFieldCorrect?.quizTaskFieldOption?.quizTaskFieldOptionId;
    set(
      formItem,
      'field.quizTaskFieldOptions',
      formItem.field.quizTaskFieldOptions.map((option) => ({
        ...option,
        isCorrect: option.quizTaskFieldOptionId === correctId,
        quizTaskFieldOptionId: option.quizTaskFieldOptionId.includes('new') ? undefined : option.quizTaskFieldOptionId,
        trigger: {
          ...option.trigger,
          items: (values.byTrigger[option.quizTaskFieldOptionId]?.triggerItemIds || []).map((itemId, itemIndex) => ({
            item: createFormItem(itemId, values),
            order: itemIndex,
          })),
        },
      }))
    );
  }

  set(formItem, 'field.__typename', FORM_TO_TASK_FIELD_TYPENAME[fieldTypename] || fieldTypename);

  return formItem;
};

const validationSchema = YupObject().shape({
  title: YupString().required('Form name is required'),
});

export type FormBuilderValues = {
  formId?: string;
  title: string;
  description: string;
  successMessage: string;
  redirectUrl: string;
  closedMessage: string;
  dateClosed: Date | null;
  isListed: boolean;
  allowMultipleSubmissions: boolean;
  formItems: FormItemsDeserialize;
};

export const FormBuilder = memo(() => {
  const { theme, isMobile } = useCampfireTheme();
  const [fid] = useQueryParam('fid', StringParam);
  const history = useHistory();
  const apiUrl = useApiUrl();
  const { setSnackbar } = useSnackbar();
  const { getManagerIdentity } = useUser();
  const saveFormMutation = useCampfireFetch({ defer: true });

  const [getForm, { data, loading }] = useCampfireLazyQuery<GetForm, GetFormVariables>(GET_FORM);

  useEffect(() => {
    if (fid) {
      getForm({
        variables: {
          formId: fid,
        },
      });
    }
  }, [fid, getForm]);

  const initValues: FormBuilderValues = useMemo(() => {
    return {
      formId: fid ?? undefined,
      title: data?.vm.form?.title ?? 'New Form',
      description: data?.vm.form?.description ?? '',
      successMessage: data?.vm.form?.successMessage ?? '',
      redirectUrl: data?.vm.form?.redirectUrl ?? '',
      closedMessage: data?.vm.form?.closedMessage ?? '',
      dateClosed: data?.vm.form?.dateClosed ? unpackToDate(data.vm.form.dateClosed).toJSDate() : null,
      isListed: data?.vm.form?.isListed ?? true,
      allowMultipleSubmissions: data?.vm.form?.allowMultipleSubmissions ?? false,
      formItems: deserializeFormItems(data?.vm.form?.formItems),
    };
  }, [data, fid]);

  async function handleSave(values: FormBuilderValues) {
    const orderedFormItems = values.formItems.allIds
      .filter((itemId) => {
        const triggerOption = values.formItems.byTriggerOption[itemId];
        if (triggerOption) {
          const trigger = values.formItems.byTrigger[triggerOption];
          if (trigger && trigger.optionType === 'crossTaskDynamic') {
            return !itemId.startsWith('new');
          }
          return !itemId.startsWith('new');
        }
        return true;
      })
      .map((itemId) => createFormItem(itemId, values.formItems))
      .map((item, formItemIndex) => set(item, 'order', formItemIndex));

    const formData = {
      formId: values.formId,
      managerId: getManagerIdentity().managerId,
      title: values.title,
      description: values.description || '',
      successMessage: values.successMessage || undefined,
      redirectUrl: values.redirectUrl || undefined,
      closedMessage: values.closedMessage || undefined,
      dateClosed: values.dateClosed || undefined,
      isListed: values.isListed,
      allowMultipleSubmissions: values.allowMultipleSubmissions,
      formItems: orderedFormItems,
    };

    try {
      const res = await saveFormMutation.run({
        url: `${apiUrl}/vm/form/save`,
        method: 'post',
        data: formData,
      });

      if (!res.ok) {
        setSnackbar({
          open: true,
          message: res.data?.message || 'Unable to save form',
          variant: 'error',
        });
        return;
      }

      setSnackbar({
        open: true,
        message: 'Form saved successfully',
        variant: 'success',
      });

      history.push('/admin/forms');
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Unable to save form',
        variant: 'error',
      });
    }
  }

  return (
    <FormBuilderDialogProvider>
      <Formik enableReinitialize initialValues={initValues} validationSchema={validationSchema} onSubmit={handleSave}>
        {({ values }) => {
          return (
            <Form style={{ width: '100%', minHeight: '100vh', backgroundColor: theme.palette.grey[100] }}>
              <AdminConsoleVolunteerProfileHeader>
                <FormBuilderHeader formName={values.title} disabled={loading || saveFormMutation.loading} />
              </AdminConsoleVolunteerProfileHeader>

              <Grid container justify='center'>
                <Grid item xs={10} sm={6}>
                  <FormDetailsCard isNew={!fid} disabled={loading || saveFormMutation.loading} />
                  {isMobile && <FormControlsContent />}
                  <FormListItemsForm />
                </Grid>

                {!isMobile && (
                  <Grid item sm={3} style={{ paddingLeft: '16px' }}>
                    <FormControlsCard />
                  </Grid>
                )}
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </FormBuilderDialogProvider>
  );
});
